import { useKeycloak } from '@react-keycloak/web';
import { ChevronRight, LogOut, Menu, Search, User } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useCommandPalette } from '@/components/CommandPalette';
import { DensityToggle } from '@/components/DensityToggle';
import { SourceHealthIndicator } from '@/components/SourceHealthIndicator';
import { ThemeToggle } from '@/components/ThemeToggle';
import { WorkspacePath } from '@/components/WorkspacePath';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { routeTitle, routeTrail } from '@/lib/nav.js';
import { useWorkspace } from '@/lib/workspace.js';

/** Stand des Builds (vom CI gesetzt), fuer Rueckfragen beim Support. */
const BUILD_HASH = ((import.meta as { env?: Record<string, string | undefined> }).env?.VITE_BUILD_HASH ?? 'dev').slice(0, 8);

/** Zwei Buchstaben fuer den Avatar: Vor- und Nachname, sonst der Anfang des Namens. */
function getInitials(value: string): string {
  const parts = value.replace(/@.*$/u, '').split(/[\s._-]+/u).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0].charAt(0);
  const second = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
  return (first + second).toUpperCase();
}

/**
 * Erkennt grob, ob der User auf einer Apple-Plattform laeuft, um das
 * Tastatur-Hint richtig zu rendern (⌘K vs Ctrl+K). Defaults to ⌘K, falls
 * navigator nicht verfuegbar ist (SSR-safe).
 */
function detectMacPlatform(): boolean {
  if (typeof navigator === 'undefined') return false;
  const plat = navigator.platform ?? '';
  const ua = navigator.userAgent ?? '';
  return /Mac|iPhone|iPad|iPod/i.test(plat) || /Macintosh/i.test(ua);
}

interface AppHeaderProps {
  onOpenMobileMenu: () => void;
  /** Toggle der Desktop-Sidebar (Burger-Menue auch >= md). */
  onToggleDesktopSidebar?: () => void;
}

export function AppHeader({
  onOpenMobileMenu,
  onToggleDesktopSidebar,
}: AppHeaderProps): JSX.Element {
  const { keycloak } = useKeycloak();
  const location = useLocation();
  const title = routeTitle(location.pathname);
  const trail = routeTrail(location.pathname);
  const palette = useCommandPalette();
  const ws = useWorkspace();

  // Plattform-Detection einmalig nach Mount (vermeidet SSR-Mismatch).
  const [isMac, setIsMac] = useState(false);
  useEffect(() => {
    setIsMac(detectMacPlatform());
  }, []);

  const shortcutLabel = isMac ? '⌘K' : 'Ctrl+K';

  // Angezeigt wird der Name aus Keycloak; die E-Mail steht nur im Menue.
  const token = keycloak.tokenParsed as Record<string, unknown> | undefined;
  const str = (k: string): string | undefined => {
    const v = token?.[k];
    return typeof v === 'string' && v.trim() ? v.trim() : undefined;
  };
  const fullName = str('name') ?? ([str('given_name'), str('family_name')].filter(Boolean).join(' ') || undefined);
  const userName = fullName ?? str('preferred_username') ?? str('email') ?? 'Benutzer';
  const userEmail = str('email');
  const initial = getInitials(userName);

  return (
    <header className="flex h-16 shrink-0 items-center justify-between gap-2 border-b border-t-4 border-b-border border-t-brand-500 bg-background px-4 md:px-6">
      <div className="flex items-center gap-2">
        {/* Burger-Menue: < md oeffnet das Sheet, >= md togglet die Sidebar. */}
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden"
          onClick={onOpenMobileMenu}
          aria-label="Menü öffnen"
        >
          <Menu className="h-5 w-5" />
        </Button>
        {onToggleDesktopSidebar ? (
          <Button
            variant="ghost"
            size="icon"
            className="hidden md:inline-flex"
            onClick={onToggleDesktopSidebar}
            aria-label="Sidebar ein-/ausblenden"
          >
            <Menu className="h-5 w-5" />
          </Button>
        ) : null}
        {/* Mit Gliederung steht hier der Arbeitsbereich; die Seite nennt ihren Titel selbst. */}
        {ws ? <WorkspacePath /> : null}
        {!ws && title.length > 0 && (
          <nav aria-label="Pfad" className="hidden items-center gap-1.5 text-sm md:flex">
            {trail.map((t) => (
              <span key={t} className="flex items-center gap-1.5 text-muted-foreground">
                {t}
                <ChevronRight className="h-3.5 w-3.5 opacity-60" aria-hidden />
              </span>
            ))}
            <span className="font-medium text-foreground" aria-current="page">{title}</span>
          </nav>
        )}
      </div>

      {/* Command-Palette-Trigger (MR 2) */}
      <div className="flex flex-1 justify-center md:justify-end">
        {/* Mobile: nur Icon */}
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden"
          onClick={() => palette.setOpen(true)}
          aria-label="Suchen oder Aktion ausführen"
        >
          <Search className="h-5 w-5" />
        </Button>

        {/* Desktop: voller Trigger */}
        <button
          type="button"
          onClick={() => palette.setOpen(true)}
          className="hidden h-9 w-full max-w-sm items-center gap-2 rounded-md border border-input bg-background px-3 text-sm text-muted-foreground ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:flex"
          aria-label={`Suchen oder Aktion ausführen (${shortcutLabel})`}
        >
          <Search className="h-4 w-4" />
          <span className="flex-1 text-left">Suchen oder Aktion...</span>
          <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
            {shortcutLabel}
          </kbd>
        </button>
      </div>

      <div className="flex items-center gap-1">
        <SourceHealthIndicator />
        <DensityToggle />
        <ThemeToggle />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="flex h-9 items-center gap-2 px-2"
              aria-label="Benutzer-Menü"
            >
              <Avatar className="h-7 w-7">
                <AvatarFallback className="bg-brand-500 text-[11px] font-semibold text-white">
                  {initial}
                </AvatarFallback>
              </Avatar>
              <span className="hidden text-sm font-medium md:inline">{userName}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="flex flex-col">
                <span className="text-sm font-medium">{userName}</span>
                {userEmail !== undefined && userEmail !== userName && (
                  <span className="text-xs font-normal text-muted-foreground">
                    {userEmail}
                  </span>
                )}
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem disabled>
              <User className="h-4 w-4" />
              <span>Profil</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => void keycloak.logout({ redirectUri: `${window.location.origin}/` })}>
              <LogOut className="h-4 w-4" />
              <span>Abmelden</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <div className="px-2 py-1 text-[11px] text-muted-foreground">CiviPort · Version {BUILD_HASH}</div>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
